import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  PaDataTable,
  PaEmptyState,
  PaSkeleton,
  PaStatusBadge,
} from "components/platform-admin";
import { fetchPlatformStripeCustomers } from "store/services/platformAdmin.services";
import { formatCurrency } from "./billingOverview.utils";
import {
  filterStripeCustomerRows,
  STRIPE_CUSTOMER_FILTERS,
} from "../platformAdminNavigation.utils";
import StripeDashboardLink from "./StripeDashboardLink";
import {
  formatStripeCustomerStatusLabel,
  formatStripeTimestamp,
  getStripeCustomerStatusVariant,
  getStripeLinkStatusVariant,
  STRIPE_CUSTOMER_LINK_FILTERS,
} from "./stripeManagement.utils";

const FilterButton = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
      active
        ? "bg-indigo-600 text-white"
        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
    }`}
  >
    {children}
  </button>
);

const SummaryCard = ({ label, value }) => (
  <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
    <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
    <p className="mt-1 text-lg font-semibold text-gray-900">{value}</p>
  </div>
);

const StripeCustomersTab = ({
  initialCustomerFilter = "all",
  onCustomerFilterChange = null,
  onSummaryChange = null,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  const [search, setSearch] = useState("");
  const [linkStatus, setLinkStatus] = useState("all");
  const [customerFilter, setCustomerFilter] = useState(initialCustomerFilter);

  useEffect(() => {
    setCustomerFilter(initialCustomerFilter);
  }, [initialCustomerFilter]);

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await fetchPlatformStripeCustomers({
        search: search.trim() || undefined,
        link_status: linkStatus,
      });
      setPayload(data);
      onSummaryChange?.(data?.summary ?? null, data?.stripe_dashboard ?? null);
    } catch (err) {
      setPayload(null);
      onSummaryChange?.(null, null);
      setError(err?.message || "Failed to load Stripe customers");
    } finally {
      setLoading(false);
    }
  }, [linkStatus, onSummaryChange, search]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      loadCustomers();
    }, search ? 250 : 0);

    return () => clearTimeout(timeout);
  }, [loadCustomers, search]);

  const rows = useMemo(() => {
    const customers = payload?.customers || [];
    return filterStripeCustomerRows(customers, customerFilter);
  }, [customerFilter, payload]);

  const summary = payload?.summary;
  const showOutstandingColumn =
    customerFilter === "outstanding" || rows.some((row) => (row.outstanding_cents || 0) > 0);

  const handleCustomerFilterChange = (nextFilter) => {
    setCustomerFilter(nextFilter);
    onCustomerFilterChange?.(nextFilter);
  };

  return (
    <div className="space-y-6">
      <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Stripe customer tenants</h2>
            <p className="mt-1 text-sm text-gray-500">
              Filter by billing state, then open a tenant&apos;s billing tab for payment history
              and subscription details.
            </p>
          </div>
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search tenant, customer ID, plan…"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm lg:max-w-xs"
          />
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {STRIPE_CUSTOMER_FILTERS.map((filter) => (
            <FilterButton
              key={filter.id}
              active={customerFilter === filter.id}
              onClick={() => handleCustomerFilterChange(filter.id)}
            >
              {filter.label}
            </FilterButton>
          ))}
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          {STRIPE_CUSTOMER_LINK_FILTERS.map((filter) => (
            <FilterButton
              key={filter.id}
              active={linkStatus === filter.id}
              onClick={() => setLinkStatus(filter.id)}
            >
              {filter.label}
            </FilterButton>
          ))}
        </div>

        {summary ? (
          <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-6">
            <SummaryCard label="Total" value={summary.total ?? 0} />
            <SummaryCard label="Active customer ID" value={summary.active_customer_id ?? 0} />
            <SummaryCard label="Historical only" value={summary.historical_only ?? 0} />
            <SummaryCard label="Paying" value={summary.paying ?? 0} />
            <SummaryCard label="With invoice history" value={summary.with_invoice_history ?? 0} />
            <SummaryCard label="Open invoices" value={summary.with_outstanding ?? 0} />
          </div>
        ) : null}
      </section>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {loading ? (
        <PaSkeleton variant="table" lines={8} />
      ) : rows.length === 0 ? (
        <PaEmptyState
          title="No Stripe customers found"
          message="Try a different filter or search term."
        />
      ) : (
        <section className="rounded-xl border border-gray-200 bg-white shadow-sm">
          {payload?.truncated ? (
            <div className="border-b border-amber-100 bg-amber-50 px-5 py-3 text-sm text-amber-900">
              Showing the first {payload.limit} Stripe customer tenants. Refine search to narrow
              results.
            </div>
          ) : null}

          <PaDataTable
            columns={[
              {
                key: "name",
                label: "Tenant",
                render: (row) => (
                  <Link
                    to={`/tenants/${row.id}?tab=billing`}
                    className="font-medium text-indigo-600 hover:text-indigo-700"
                  >
                    {row.name}
                  </Link>
                ),
              },
              {
                key: "plan_name",
                label: "Plan",
                render: (row) => row.plan_name || "Unassigned",
              },
              {
                key: "link_status",
                label: "Stripe link",
                render: (row) => (
                  <div className="flex flex-col items-start gap-1">
                    <PaStatusBadge
                      label={row.link_status_label || row.link_status}
                      variant={getStripeLinkStatusVariant(row.link_status)}
                    />
                    {row.stripe_dashboard_customer_url ? (
                      <StripeDashboardLink
                        href={row.stripe_dashboard_customer_url}
                        title="Open customer in Stripe"
                      >
                        {row.stripe_customer_id}
                      </StripeDashboardLink>
                    ) : row.stripe_dashboard_last_invoice_url ? (
                      <StripeDashboardLink
                        href={row.stripe_dashboard_last_invoice_url}
                        title="Open last invoice in Stripe"
                      >
                        Last invoice
                      </StripeDashboardLink>
                    ) : null}
                  </div>
                ),
              },
              {
                key: "subscription_status",
                label: "Subscription",
                render: (row) => {
                  const badge = (
                    <PaStatusBadge
                      label={formatStripeCustomerStatusLabel(row.subscription_status)}
                      variant={getStripeCustomerStatusVariant(row)}
                    />
                  );

                  if (row.stripe_dashboard_subscription_url) {
                    return (
                      <a
                        href={row.stripe_dashboard_subscription_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-block"
                        title="Open subscription in Stripe"
                      >
                        {badge}
                      </a>
                    );
                  }

                  return badge;
                },
              },
              ...(showOutstandingColumn
                ? [
                    {
                      key: "outstanding_cents",
                      label: "Outstanding",
                      headerClassName: "text-right",
                      cellClassName: "text-right",
                      render: (row) => {
                        if (!row.outstanding_cents) {
                          return "—";
                        }

                        const amount = formatCurrency(row.outstanding_cents, "AUD");

                        if (row.stripe_dashboard_customer_url && row.open_invoice_count > 0) {
                          return (
                            <StripeDashboardLink
                              href={row.stripe_dashboard_customer_url}
                              className="inline-flex items-center justify-end gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-700"
                              title="Open customer invoices in Stripe"
                            >
                              {amount}
                            </StripeDashboardLink>
                          );
                        }

                        return amount;
                      },
                    },
                  ]
                : []),
              {
                key: "last_invoice_at",
                label: "Last invoice",
                render: (row) => {
                  const formatted = formatStripeTimestamp(row.last_invoice_at);

                  if (!formatted || formatted === "—") {
                    return "—";
                  }

                  if (row.stripe_dashboard_last_invoice_url) {
                    return (
                      <StripeDashboardLink
                        href={row.stripe_dashboard_last_invoice_url}
                        title="Open invoice in Stripe"
                      >
                        {formatted}
                      </StripeDashboardLink>
                    );
                  }

                  return formatted;
                },
              },
            ]}
            rows={rows}
            rowKey="id"
          />
        </section>
      )}
    </div>
  );
};

export default StripeCustomersTab;
